import React, { useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import {
  Ticket,
  Dumbbell,
  Stethoscope,
  Clock,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Check,
  X,
} from 'lucide-react';

const SLIDES = [
  {
    id: 'welcome',
    icon: Sparkles,
    accent: 'from-teal-500 via-emerald-500 to-emerald-600',
    eyebrow: 'BEM-VINDO AO VITAL PERSONAL',
    title: 'Sua saúde ganhou um novo nível.',
    body: (
      <>
        <p className="text-slate-600 leading-relaxed">
          Como assinante <strong>Vital Personal</strong>, você tem acesso ao ecossistema mais completo
          de saúde e performance do Brasil — <strong>academias ilimitadas</strong> e
          <strong> profissionais parceiros</strong> na palma da mão.
        </p>
      </>
    ),
    highlight: null,
  },
  {
    id: 'gyms',
    icon: Dumbbell,
    accent: 'from-blue-500 via-blue-600 to-indigo-600',
    eyebrow: 'REDE DE ACADEMIAS',
    title: 'Check-ins ilimitados. Em qualquer academia parceira.',
    body: (
      <p className="text-slate-600 leading-relaxed">
        Treine <strong>quantas vezes quiser</strong>, em <strong>qualquer academia</strong>
        da rede Vital. Sem cotas, sem limites, sem restrições de dia da semana.
      </p>
    ),
    highlight: (
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-4 text-white shadow-lg">
        <p className="text-[10px] uppercase tracking-widest font-bold opacity-80">Check-ins/mês</p>
        <p className="text-5xl font-black leading-none">∞</p>
        <p className="text-xs mt-1 opacity-90">Ilimitados na rede parceira</p>
      </div>
    ),
  },
  {
    id: 'vitalpass',
    icon: Ticket,
    accent: 'from-teal-500 to-emerald-600',
    eyebrow: 'VITALPASS',
    title: '1 VITALPASS = 1 atendimento profissional.',
    body: (
      <>
        <p className="text-slate-600 leading-relaxed mb-3">
          Cada VITALPASS é uma <strong>ficha premium</strong> que você usa para agendar
          um profissional parceiro à sua escolha:
        </p>
        <div className="grid grid-cols-2 gap-2 text-xs">
          {[
            'Personal Trainer',
            'Fisioterapeuta',
            'Nutricionista',
            'Psicólogo',
            'Esteticista',
            'Massagista',
          ].map((cat) => (
            <div key={cat} className="flex items-center gap-2 bg-teal-50 rounded-lg px-3 py-1.5">
              <Check className="w-3.5 h-3.5 text-teal-600 flex-shrink-0" />
              <span className="text-slate-700 font-medium">{cat}</span>
            </div>
          ))}
        </div>
      </>
    ),
    highlight: (
      <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-xl p-4 text-white shadow-lg">
        <div className="flex items-center gap-2 mb-1">
          <Ticket className="w-5 h-5" />
          <p className="text-[10px] uppercase tracking-widest font-bold opacity-90">SEU SALDO</p>
        </div>
        <p className="text-4xl font-black leading-none">2 a 5</p>
        <p className="text-xs mt-1 opacity-90">tokens por mês (varia por plano)</p>
      </div>
    ),
  },
  {
    id: 'cycle',
    icon: Clock,
    accent: 'from-amber-500 via-orange-500 to-orange-600',
    eyebrow: 'CICLO DE 30 DIAS',
    title: 'Use ou perca: seus VITALPASS renovam a cada mês.',
    body: (
      <>
        <p className="text-slate-600 leading-relaxed mb-3">
          Todo mês seu saldo é <strong>renovado automaticamente</strong> conforme seu plano.
          Tokens não utilizados <strong>não acumulam</strong> — eles zeram na renovação.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
          💡 <strong>Dica:</strong> agende seus atendimentos logo no início do ciclo para
          aproveitar 100% dos seus tokens antes de expirarem.
        </div>
      </>
    ),
    highlight: null,
  },
  {
    id: 'howto',
    icon: Stethoscope,
    accent: 'from-emerald-500 to-teal-600',
    eyebrow: 'COMO USAR AGORA',
    title: 'Comece em 3 passos simples.',
    body: (
      <div className="space-y-3">
        {[
          {
            num: 1,
            title: 'Abra o menu "Profissionais"',
            desc: 'Explore a rede completa com filtros por categoria, cidade e especialidade.',
          },
          {
            num: 2,
            title: 'Escolha e clique "Solicitar"',
            desc: 'Confirmamos o uso de 1 VITALPASS antes de registrar o pedido.',
          },
          {
            num: 3,
            title: 'Acompanhe em "VITALPASS"',
            desc: 'Veja seu saldo, histórico e data de renovação no seu menu lateral.',
          },
        ].map((step) => (
          <div key={step.num} className="flex gap-3 items-start">
            <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-black text-sm shadow">
              {step.num}
            </div>
            <div className="flex-1">
              <p className="font-bold text-slate-900 text-sm">{step.title}</p>
              <p className="text-xs text-slate-500 leading-relaxed">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
    ),
    highlight: null,
  },
];

const PersonalOnboardingTour = ({ open, onClose, userId }) => {
  const [current, setCurrent] = useState(0);
  const slide = SLIDES[current];
  const Icon = slide.icon;
  const isLast = current === SLIDES.length - 1;
  const isFirst = current === 0;

  const finish = () => {
    if (userId) {
      try {
        localStorage.setItem(`vital_onboarding_personal_${userId}`, '1');
      } catch (_) { /* ignore quota errors */ }
    }
    setCurrent(0);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && finish()}>
      <DialogContent
        className="max-w-lg p-0 overflow-hidden border-0"
        style={{ maxHeight: '92dvh' }}
        data-testid="personal-onboarding-tour"
      >
        {/* Skip button */}
        <button
          onClick={finish}
          data-testid="onboarding-skip"
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/80 hover:bg-white text-slate-600 flex items-center justify-center shadow-sm"
          aria-label="Pular tutorial"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Progress dots */}
        <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5">
          {SLIDES.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === current
                  ? 'w-6 bg-white'
                  : i < current
                    ? 'w-1.5 bg-white/70'
                    : 'w-1.5 bg-white/30'
              }`}
              data-testid={`onboarding-dot-${i}`}
            />
          ))}
        </div>

        {/* Header hero (gradient) */}
        <div className={`bg-gradient-to-br ${slide.accent} px-6 pt-14 pb-6 text-white relative overflow-hidden`}>
          <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
          <div className="relative">
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-3">
              <Icon className="w-7 h-7 text-white" />
            </div>
            <p className="text-[10px] uppercase tracking-widest font-bold opacity-80">{slide.eyebrow}</p>
            <h2
              className="text-2xl font-black leading-tight mt-1"
              style={{ fontFamily: 'Chivo, sans-serif' }}
              data-testid="onboarding-title"
            >
              {slide.title}
            </h2>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4" data-testid={`onboarding-slide-${slide.id}`}>
          {slide.body}
          {slide.highlight && <div>{slide.highlight}</div>}
        </div>

        {/* Footer nav */}
        <div className="px-6 pb-5 flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrent((c) => Math.max(0, c - 1))}
            disabled={isFirst}
            data-testid="onboarding-prev"
            className={`text-slate-500 ${isFirst ? 'invisible' : ''}`}
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
          </Button>
          <p className="text-xs text-slate-400 font-semibold">
            {current + 1} / {SLIDES.length}
          </p>
          {isLast ? (
            <Button
              onClick={finish}
              data-testid="onboarding-finish"
              className="bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white font-bold shadow"
            >
              Começar agora <Check className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={() => setCurrent((c) => Math.min(SLIDES.length - 1, c + 1))}
              data-testid="onboarding-next"
              className="bg-slate-900 hover:bg-slate-800 text-white font-bold"
            >
              Avançar <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PersonalOnboardingTour;
